/**
 * Child-only sync types and their parents (SCH-3122, SCH-3144).
 *
 * A child-only type runs only as a child job its parent creates with the
 * student, section, user or record it acts on, so a bare run of it from a
 * dashboard has no target. Which types those are is a static fact about each
 * type, so it lives here and ships inside the web bundle: a web build that
 * deploys ahead of the backend still knows them (SCH-3144).
 *
 * The backend's Sync Catalog says the same thing on each descriptor
 * (`SyncStep.childOf`). The two are kept equal by
 * `apps/backend/tests/services/platform-data-sync/catalog/shared-types-parity.test.ts`,
 * which also pins each parent's label below to its catalog label. Typed as
 * plain strings, like `syncType`, so a reader can look up any column value
 * without a cast.
 */
import type { SyncParentRef } from './sync'

/** Child-only sync type → the parent types whose jobs create it. */
export const CHILD_SYNC_TYPE_PARENTS: Readonly<Record<string, readonly string[]>> = {
  SECTION_CONTENT: ['CONTENT_CHANGE_DETECTION'],
  CATEGORY_CONTENT: ['CONTENT_CHANGE_DETECTION'],
  USER_CONTENT_SYNC: ['USER_CONTENT_CHANGE_DETECTION'],
  SCHOOL_USER_PROFILES: ['SCHOOL_USER_PROFILES_BULK'],
  SINGLE_DIRECTORY_MEMBERS: ['DIRECTORY_MEMBERS'],
  SINGLE_STUDENT_ASSIGNMENTS: ['STUDENT_ASSIGNMENTS_BULK'],
  SINGLE_SECTION_ASSIGNMENTS: ['ASSIGNMENTS'],
  SINGLE_STUDENT_GRADED_ASSIGNMENTS: ['STUDENT_GRADED_ASSIGNMENTS_BULK'],
  SINGLE_STUDENT_SECTIONS: ['STUDENT_SECTIONS_BULK'],
  SECTION_ROSTER: [
    'SECTIONS_ACADEMIC',
    'SECTIONS_ACTIVITIES',
    'SECTIONS_ADVISORY',
    'SECTIONS_ATHLETICS',
    'SECTIONS_DORMS',
    'SECTIONS_GROUPS',
    'ACADEMIC_ENROLLMENT_DELTA'
  ],
  SINGLE_CONTENT_ITEM_PUBLISH: ['SCHEDULED_CONTENT_PUBLISHING', 'CONTENT_AUTO_CONVERT_SECTION'],
  ENROLLMENT_CHECKLIST_DETAIL: ['ENROLLMENT_CHECKLISTS_BULK'],
  SCHOOL_USER_PROFILES_PAGE: ['SCHOOL_USER_PROFILES_BULK', 'SCHOOL_USER_PROFILES_CHANGED'],
  PHOTO_ALBUMS_SECTION: ['PHOTO_ALBUMS']
}

/**
 * Per-target types with no parent sync, so they are not child-only: an app
 * request creates them for one user (SINGLE_USER_DIRECTORY) or one group
 * (GROUP_MEMBERS). A bare run from a dashboard has no target either.
 */
export const RUN_NEEDS_A_TARGET_SYNC_TYPES: readonly string[] = ['SINGLE_USER_DIRECTORY', 'GROUP_MEMBERS']

/** A sync type as the catalog labels it: `STUDENT_ASSIGNMENTS_BULK` → "Student Assignments Bulk". */
function syncTypeTitle(syncType: string): string {
  return syncType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/** The parents a child-only type runs as part of, with their labels, or null when it runs standalone. */
export function childSyncTypeParents(syncType: string): SyncParentRef[] | null {
  const parents = CHILD_SYNC_TYPE_PARENTS[syncType]
  if (!parents || parents.length === 0) return null
  return parents.map(parent => ({ syncType: parent, label: syncTypeTitle(parent) }))
}

/** True when a bare run of the type, from a dashboard Run button, would have no target. */
export function isRunWithoutTargetBlocked(syncType: string): boolean {
  return childSyncTypeParents(syncType) !== null || RUN_NEEDS_A_TARGET_SYNC_TYPES.includes(syncType)
}
